/**
 * 季度上下文：当前在哪个项目 / 哪一季 / 哪份计划。
 *
 * 这是所有主线页面的共同前提。用户在工作台切了季，进布置台还是那一季——
 * 不能让他在每个页面重选一次，那是打断心智最常见的方式。
 *
 * 选择结果落 localStorage，刷新后还在原地。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { fetchAllPlans, fetchCycles, fetchProjects } from '@/services/seasonApi'
import type { BreedingPlan, BreedingProject, ResearchCycle } from '@/services/types'

const STORAGE_KEY = 'cbms_season_scope'

interface SeasonScope {
  projectId?: string
  cycleId?: string
  planId?: string
}

interface SeasonContextValue {
  loading: boolean
  error?: string

  projects: BreedingProject[]
  cycles: ResearchCycle[]
  allPlans: BreedingPlan[]
  plans: BreedingPlan[]

  project?: BreedingProject
  cycle?: ResearchCycle
  plan?: BreedingPlan

  setProject: (projectId: string) => void
  setCycle: (cycleId?: string) => void
  setPlan: (planId?: string) => void
  reload: () => void
}

const SeasonContext = createContext<SeasonContextValue | null>(null)

function readScope(): SeasonScope {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as SeasonScope) : {}
  } catch {
    return {}
  }
}

function writeScope(scope: SeasonScope) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(scope))
}

export function SeasonProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [projects, setProjects] = useState<BreedingProject[]>([])
  const [cycles, setCycles] = useState<ResearchCycle[]>([])
  const [plans, setPlans] = useState<BreedingPlan[]>([])
  const [scope, setScope] = useState<SeasonScope>(readScope)
  const [version, setVersion] = useState(0)

  const reload = useCallback(() => setVersion((v) => v + 1), [])

  // 项目列表
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchProjects()
      .then((res) => {
        if (cancelled) return
        const list = res.items ?? []
        setProjects(list)
        setScope((prev) => {
          // 没选过或选的项目已不存在时，落到第一个
          if (prev.projectId && list.some((p) => p.id === prev.projectId)) return prev
          const next = { projectId: list[0]?.id }
          writeScope(next)
          return next
        })
        setError(undefined)
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail ?? '项目列表加载失败')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [version])

  // 季与计划随项目变
  useEffect(() => {
    if (!scope.projectId) {
      setCycles([])
      setPlans([])
      return
    }
    let cancelled = false
    Promise.all([
      fetchCycles({ project_id: scope.projectId }),
      fetchAllPlans({ project_id: scope.projectId }),
    ])
      .then(([cycleRes, planRes]) => {
        if (cancelled) return
        const cycleList = cycleRes.items ?? []
        const planList = planRes.items ?? []
        setCycles(cycleList)
        setPlans(planList)

        setScope((prev) => {
          const next = { ...prev }
          let changed = false
          // 季默认落到最新的一季
          if (!next.cycleId || !cycleList.some((c) => c.id === next.cycleId)) {
            next.cycleId = cycleList[0]?.id
            changed = true
          }
          const scoped = planList.filter((p) => !next.cycleId || p.cycle_id === next.cycleId)
          if (!next.planId || !scoped.some((p) => p.id === next.planId)) {
            next.planId = scoped[0]?.id
            changed = true
          }
          if (changed) writeScope(next)
          return changed ? next : prev
        })
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.detail ?? '季度信息加载失败')
      })
    return () => {
      cancelled = true
    }
  }, [scope.projectId, version])

  const setProject = useCallback((projectId: string) => {
    // 换项目时清掉季与计划，避免带着上个项目的季
    const next = { projectId }
    writeScope(next)
    setScope(next)
  }, [])

  const setCycle = useCallback((cycleId?: string) => {
    setScope((prev) => {
      const next = { ...prev, cycleId, planId: undefined }
      writeScope(next)
      return next
    })
  }, [])

  const setPlan = useCallback((planId?: string) => {
    setScope((prev) => {
      const next = { ...prev, planId }
      writeScope(next)
      return next
    })
  }, [])

  const value = useMemo<SeasonContextValue>(() => {
    const scopedPlans = scope.cycleId
      ? plans.filter((p) => p.cycle_id === scope.cycleId)
      : plans
    return {
      loading,
      error,
      projects,
      cycles,
      allPlans: plans,
      plans: scopedPlans,
      project: projects.find((p) => p.id === scope.projectId),
      cycle: cycles.find((c) => c.id === scope.cycleId),
      plan: scopedPlans.find((p) => p.id === scope.planId),
      setProject,
      setCycle,
      setPlan,
      reload,
    }
  }, [loading, error, projects, cycles, plans, scope, setProject, setCycle, setPlan, reload])

  return <SeasonContext.Provider value={value}>{children}</SeasonContext.Provider>
}

export function useSeason(): SeasonContextValue {
  const ctx = useContext(SeasonContext)
  if (!ctx) throw new Error('useSeason 必须在 SeasonProvider 内使用')
  return ctx
}
